import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import React from "react";

import { MeetingNoteVisibility } from "@performyard/models";
import { toast } from "@performyard/py-components";
import type {
	ActionItemSearchServiceFrontendResponse,
	CreateActionItemRequestJson,
	CreateAgendaItemRequestJson,
	CreateMeetingRequestJson,
	CreateMeetingWorkspaceRequestJson,
	GetMeetingWorkspacesSearchServiceFrontendResponse,
	GetMeetingsSearchServiceFrontendResponse,
	GetUpcomingMeetingsResponseFrontend,
	MeetingExtendedModelJson,
	MeetingModelJson,
	UpdateActionItemRequestJson,
	UpdateAgendaItemRequestJson,
	UpdateMeetingNoteRequestJson,
	UpdateMeetingRequestJson,
	UpdateMeetingWorkspaceRequestJson,
} from "@performyard/service-contracts";

import { getCalendarSuccessToastMessage, getErrorToastMessage } from "../../../pages/Meetings/MeetingsTable/utils";

import {
	actionItemsSearchQuery,
	latestMeetingCarryOverItemsQuery,
	meetingActionItemsQuery,
	meetingAgendaItemsQuery,
	meetingDetailsQuery,
	meetingNotesQuery,
	meetingTranscriptQuery,
	meetingWorkspaceDetailsQuery,
	meetingWorkspacesSearchQuery,
	meetingsSearchQuery,
	upcomingMeetingsQuery,
} from "../../../api/queries";
import {
	type GetActionItemsSearchParams,
	type GetMeetingWorkspacesParams,
	type GetMeetingsSearchParams,
	type GetUpcomingMeetingsParams,
	addMeetingActionItem,
	addMeetingAgendaItem,
	addWorkspaceMeeting,
	editMeetingActionItem,
	editMeetingAgendaItem,
	editWorkspaceMeeting,
	patchMeetingWorkspace,
	postMeetingWorkspace,
	updateMeetingTranscript,
	updatePrivateMeetingNotes,
	updatePublicMeetingNotes,
} from "../../../api/services/defs/internal-api";

import type { PYUseMutationOptions, PYUseQueryOptions } from "../StandardQueryArguments";

export const useCreateMeetingWorkspace = (options?: PYUseMutationOptions) => {
	const client = useQueryClient();
	const { onSuccess: originalOnSuccess, ...restOptions } = options || {};
	return useMutation({
		mutationFn: async (data: CreateMeetingWorkspaceRequestJson) => postMeetingWorkspace(data),
		...restOptions,
		onSuccess: (response, variables, context, mutation) => {
			client.invalidateQueries({ queryKey: ["get-meeting-workspaces-search"] });
			originalOnSuccess?.(response, variables, context, mutation);
		},
	});
};

export const useUpdateMeetingWorkspace = (options?: PYUseMutationOptions) => {
	const client = useQueryClient();
	const { onSuccess: originalOnSuccess, ...restOptions } = options || {};
	return useMutation({
		mutationFn: async (props: { workspaceId: string; data: UpdateMeetingWorkspaceRequestJson }) =>
			patchMeetingWorkspace(props.workspaceId, props.data),
		...restOptions,
		onSuccess: (response, variables, context, mutation) => {
			client.invalidateQueries({ queryKey: ["get-meeting-workspaces-search"] });
			originalOnSuccess?.(response, variables, context, mutation);
		},
	});
};

export const useMeetingWorkspacesSearch = (
	params: GetMeetingWorkspacesParams,
	options?: PYUseQueryOptions<GetMeetingWorkspacesSearchServiceFrontendResponse>
) => {
	const query = useQuery<GetMeetingWorkspacesSearchServiceFrontendResponse>({
		...meetingWorkspacesSearchQuery(params),
		...options,
	});

	return {
		data: query?.data?.results || [],
		totalCount: query?.data?.total || 0,
		isLoading: query?.isLoading,
	};
};

export const useMeetingWorkspaceDetails = (workspaceId: string) => {
	const query = useQuery({
		...meetingWorkspaceDetailsQuery(workspaceId),
		enabled: !!workspaceId, // Only run query when workspaceId exists
	});

	return {
		data: query?.data?.[0],
		isLoading: query?.isLoading,
	};
};

export const useMeetingDetails = (meetingId: string) => {
	const query = useQuery({
		...meetingDetailsQuery(meetingId),
		enabled: !!meetingId, // Only run query when meetingId exists
	});

	return {
		data: query?.data,
		meeting: query?.data?.meeting,
		workspace: query?.data?.workspace,
		isLoading: query?.isLoading,
		error: query?.error,
	};
};

export const useMeetingAgendaItems = (meetingId: string) => {
	const query = useQuery({
		...meetingAgendaItemsQuery(meetingId),
		enabled: !!meetingId, // Only run query when meetingId exists
	});

	return {
		data: query?.data?.[0],
		isLoading: query?.isLoading,
	};
};

export const useMeetingActionItems = (meetingId: string) => {
	const query = useQuery({
		...meetingActionItemsQuery(meetingId),
		enabled: !!meetingId, // Only run query when meetingId exists
	});

	return {
		data: query?.data?.[0],
		isLoading: query?.isLoading,
	};
};

export const useMeetingNotes = (meetingId: string, visibility?: MeetingNoteVisibility) => {
	const query = useQuery({
		...meetingNotesQuery(meetingId, visibility),
		enabled: !!meetingId, // Only run query when meetingId exists
	});

	return {
		data: query?.data?.[0],
		isLoading: query?.isLoading,
	};
};

export const useMeetingTranscript = (meetingId: string) => {
	const query = useQuery({
		...meetingTranscriptQuery(meetingId),
		enabled: !!meetingId, // Only run query when meetingId exists
	});

	return {
		data: query?.data,
		isLoading: query?.isLoading,
	};
};

export const useAddWorkspaceMeeting = (
	handleCloseModal?: () => void,
	setExpandedMeetingIds?: React.Dispatch<React.SetStateAction<string[]>>,
	onCalendarError?: (error: unknown, variables: { workspaceId: string; data: CreateMeetingRequestJson }) => boolean
) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({ workspaceId, data }: { workspaceId: string; data: CreateMeetingRequestJson }) =>
			await addWorkspaceMeeting(workspaceId, data),
		onSuccess: async (data, variables) => {
			// Invalidate all agenda items and action items in case the new meeting carried over incomplete items
			await queryClient.invalidateQueries({
				predicate: (query) => {
					return [
						"get-meetings-search",
						"get-meeting-agenda-items-by-meeting",
						"get-meeting-action-items-by-meeting",
						"get-meeting-workspaces-search",
					].includes(query.queryKey[0] as string);
				},
				exact: false,
			});
			setExpandedMeetingIds?.((prev) => [...prev, data._id]);

			// Use centralized success message logic for calendar operations
			if (variables.data.addToCalendar && data.calendarEvent?.eventId) {
				const successMessage = getCalendarSuccessToastMessage({
					calendarOperationType: "created",
					operationData: variables.data,
					isInitialCreation: true,
				});
				toast.success(successMessage);
			} else {
				toast.success("Meeting Created");
			}

			handleCloseModal?.();
		},
		onError: (error, variables) => {
			// Always try calendar error handling first - let the handler decide if it's a calendar error
			if (onCalendarError) {
				const wasHandled = onCalendarError(error, variables);
				if (wasHandled) {
					return;
				}
			}
			// Otherwise show generic error message
			toast.error("Failed to Create Meeting");
		},
		meta: {
			disableDefaultErrorHandling: true,
		},
	});
};

export const useEditWorkspaceMeeting = (
	handleCloseModal?: () => void,
	onCalendarError?: (error: unknown, variables: { meetingId: string; data: UpdateMeetingRequestJson }) => boolean
) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({ meetingId, data }: { meetingId: string; data: UpdateMeetingRequestJson }) =>
			await editWorkspaceMeeting(meetingId, data),
		onSuccess: (data, variables) => {
			queryClient.invalidateQueries({
				predicate: (query) => {
					return ["get-meetings-search", "get-meeting-workspaces-search", "get-meeting-details"].includes(
						query.queryKey[0] as string
					);
				},
				exact: false,
			});

			// Use centralized success message logic
			const successMessage = getCalendarSuccessToastMessage({
				calendarOperationType: data.calendarOperationType,
				operationData: variables.data,
				isInitialCreation: false,
			});

			// Handle calendar event not found with specific warning
			if (data.calendarOperationType === "calendar_event_not_found") {
				toast.error("Meeting Updated Successfully. Connected Calendar Event Not Found.");
				handleCloseModal?.();
				return; // Early return to avoid the standard success toast
			}

			if (successMessage) {
				toast.success(successMessage);
			}
			handleCloseModal?.();
		},
		onError: (error, variables) => {
			// Always try calendar error handling first - let the handler decide if it's a calendar error
			if (onCalendarError) {
				const wasHandled = onCalendarError(error, variables);
				// If the calendar error handler didn't handle it, fall through to generic error handling
				if (wasHandled) {
					return;
				}
			}

			// Use centralized error message logic
			const errorMessage = getErrorToastMessage(variables.data);
			toast.error(errorMessage);
		},
		meta: {
			disableDefaultErrorHandling: true,
		},
	});
};

export const useMeetingsSearch = <T extends "companyMeetings" | "workspaceDetails" = "workspaceDetails">(
	params: GetMeetingsSearchParams,
	options?: PYUseQueryOptions<GetMeetingsSearchServiceFrontendResponse>
) => {
	const query = useQuery<GetMeetingsSearchServiceFrontendResponse>({
		...meetingsSearchQuery(params),
		...options,
	});

	// Type assertion based on the context of use
	// When the generic type is 'companyMeetings', we expect MeetingExtendedModelJson
	// Otherwise we get regular MeetingModelJson
	type ResultType = T extends "companyMeetings" ? MeetingExtendedModelJson[] : MeetingModelJson[];

	return {
		data: (query?.data?.results as ResultType) || [],
		totalCount: query?.data?.total || 0,
		isLoading: query?.isLoading,
		isFetching: query?.isFetching,
	};
};

export const useLatestMeetingCarryOverItems = (workspaceId: string, startDateTime: Date, enabled: boolean) => {
	const query = useQuery({
		enabled,
		...latestMeetingCarryOverItemsQuery(workspaceId, startDateTime),
	});

	return {
		data: query?.data,
		isLoading: query?.isLoading,
	};
};

export const useActionItemsSearch = (
	params: GetActionItemsSearchParams,
	options?: PYUseQueryOptions<ActionItemSearchServiceFrontendResponse>
) => {
	const query = useQuery<ActionItemSearchServiceFrontendResponse>({
		...actionItemsSearchQuery(params),
		...options,
	});

	return {
		data: query?.data?.results || [],
		totalCount: query?.data?.total || 0,
		isLoading: query?.isLoading,
	};
};

export const useAddMeetingAgendaItem = () => {
	return useMutation({
		mutationFn: async ({ data }: { data: CreateAgendaItemRequestJson }) => await addMeetingAgendaItem(data),
	});
};

export const useEditMeetingAgendaItem = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async ({ agendaItemId, data }: { agendaItemId: string; data: UpdateAgendaItemRequestJson }) =>
			await editMeetingAgendaItem(agendaItemId, data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["get-meeting-agenda-items-by-meeting"] });
		},
	});
};

export const useAddMeetingActionItem = () => {
	return useMutation({
		mutationFn: async ({ data }: { data: CreateActionItemRequestJson }) => await addMeetingActionItem(data),
	});
};

export const useEditMeetingActionItem = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async ({ actionItemId, data }: { actionItemId: string; data: UpdateActionItemRequestJson }) =>
			await editMeetingActionItem(actionItemId, data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["get-meeting-action-items-by-meeting"] });
			queryClient.invalidateQueries({ queryKey: ["get-action-items-search"] });
		},
	});
};

export const useUpdatePublicMeetingNotes = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async ({
			noteId,
			data,
		}: {
			noteId: string;
			data: UpdateMeetingNoteRequestJson;
			meetingId: string;
		}) => await updatePublicMeetingNotes(noteId, data),
		onSuccess: (_, { meetingId }) => {
			queryClient.invalidateQueries({ queryKey: ["get-meeting-notes-by-meeting", meetingId] });
			// Mark meetings search cache as stale without refetching immediately (needed for filtering)
			queryClient.invalidateQueries({
				queryKey: ["get-meetings-search"],
				refetchType: "none",
			});
		},
	});
};

export const useUpdatePrivateMeetingNotes = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async ({
			noteId,
			data,
		}: {
			noteId: string;
			data: UpdateMeetingNoteRequestJson;
			meetingId: string;
		}) => await updatePrivateMeetingNotes(noteId, data),
		onSuccess: (_, { meetingId }) => {
			queryClient.invalidateQueries({ queryKey: ["get-meeting-notes-by-meeting", meetingId] });
			// Mark meetings search cache as stale without refetching immediately (needed for filtering)
			queryClient.invalidateQueries({
				queryKey: ["get-meetings-search"],
				refetchType: "none",
			});
		},
	});
};

export const useUpdateMeetingTranscript = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async ({ meetingId, content }: { meetingId: string; content: string }) =>
			await updateMeetingTranscript(meetingId, content),
		onSuccess: (_, { meetingId }) => {
			queryClient.invalidateQueries({ queryKey: ["get-meeting-transcript", meetingId] });
		},
	});
};

export const useUpcomingMeetings = (
	params: GetUpcomingMeetingsParams,
	options?: PYUseQueryOptions<GetUpcomingMeetingsResponseFrontend>
) => {
	const query = useQuery<GetUpcomingMeetingsResponseFrontend>({
		...upcomingMeetingsQuery(params),
		...options,
	});

	return {
		data: query?.data || [],
		isLoading: query?.isLoading,
	};
};
